import { useSearchParams } from "react-router";

import type { CompanyCounts, CompanyQuery } from "../../api/companies";
import { STATUSES } from "../../domain/status";
import type { CompanyStatus } from "../../domain/status";
import { nl } from "../../i18n/nl";
import { DESKTOP_QUERY, useMediaQuery } from "../../lib/useMediaQuery";

/** `?filter=` in the URL: one choice at a time, like the sidebar and the chips. */
export type CompanyFilter = "alle" | "voor-jou" | CompanyStatus;

export const FILTERS: readonly CompanyFilter[] = ["alle", "voor-jou", ...STATUSES];

function isFilter(value: string | null): value is CompanyFilter {
  return value !== null && (FILTERS as readonly string[]).includes(value);
}

export function filterLabel(filter: CompanyFilter): string {
  if (filter === "alle") return nl.companies.filterAll;
  if (filter === "voor-jou") return nl.companies.filterForMe;
  return nl.status[filter];
}

export function toQuery(filter: CompanyFilter, q: string): CompanyQuery {
  if (filter === "alle") return { q };
  if (filter === "voor-jou") return { forMe: true, q };
  return { status: filter, q };
}

export function countFor(filter: CompanyFilter, counts: CompanyCounts): number {
  if (filter === "alle") return counts.all;
  if (filter === "voor-jou") return counts.for_me;
  return counts[filter];
}

/** Filter and search from the URL. Without `?filter=`: "Alle" on desktop, "Voor jou" on mobile. */
export function useCompanyFilters() {
  const [params, setParams] = useSearchParams();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const raw = params.get("filter");
  const filter: CompanyFilter = isFilter(raw) ? raw : desktop ? "alle" : "voor-jou";
  const q = params.get("q") ?? "";

  function update(next: { filter?: CompanyFilter; q?: string }) {
    const merged = new URLSearchParams(params);
    if (next.filter !== undefined) merged.set("filter", next.filter);
    if (next.q !== undefined) {
      if (next.q) merged.set("q", next.q);
      else merged.delete("q");
    }
    setParams(merged, { replace: true });
  }

  return { filter, q, params, update };
}
